import React from "react";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import Framework from "@beardeddevops/react.framework";
import ReactTable from "react-table";
import moment from "moment";
import {Card, CardContent} from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import ReportDateToolbar, {
	buildReportApiFilter,
	createDefaultReportDateState,
	defaultReportFilterState,
	formatDateRangeLabel,
	formatReportMoney,
} from "components/Elements/ReportFilterToggles";

const DATE_STORAGE_KEY = "dateEmployeeSales";

export default class EmployeeSalesReport extends Framework.Components.Collection {
	constructor(props) {
		let model = new API.BuildClass(Types.Locations_Sales._name, API.RequestType.READ_ALL);
		super(props, model);
		this.request = "GET_EMPLOYEE_SALES_REPORT";
		Object.assign(this.state, defaultReportFilterState, createDefaultReportDateState(DATE_STORAGE_KEY), {
			categories: [],
			columns: [],
		});
	}

	handleLayawayModeChange = async (completedLayawaysOnly) => {
		await this.setState({completedLayawaysOnly});
		await this.getReport();
	};

	handleWorkOrderModeChange = async (completedWorkOrdersOnly) => {
		await this.setState({completedWorkOrdersOnly});
		await this.getReport();
	};

	handleTimezoneModeChange = async (useLocalTimezone) => {
		await this.setState({useLocalTimezone});
		await this.getReport();
	};

	async componentDidMount() {
		await this.getReport();
	}

	handleToggleModal = (modal) => {
		this.setState({[modal]: !this.state[modal]});
	};

	handleDateChange = async (name, date) => {
		await this.setState({filterDate: moment(date).format()});
		this.handleToggleModal("showDate");
		await this.getReport();
	};

	handleCancelDateRange = () => {
		const today = moment().format("YYYY-MM-DD");
		this.setState({start_date: today, end_date: today});
		this.handleToggleModal("showDateRange");
	};

	buildColumns = (categories) => {
		const categoryColumns = (categories || []).map((category) => ({
			Header: category.category_name,
			id: `category_${category.category_key}`,
			accessor: (r) => {
				const value = r?.categories?.[category.category_key];
				return value != null ? "$" + formatReportMoney(value) : "";
			},
			sortable: false,
		}));

		return [
			{
				Header: "Seller",
				id: "seller",
				accessor: (r) => r?.seller ?? "",
				sortable: false,
			},
			{
				Header: "Salesman",
				id: "salesman",
				accessor: (r) => r?.salesman ?? "",
				sortable: false,
			},
			{
				Header: "Total Sales",
				id: "total_sales",
				accessor: (r) => "$" + formatReportMoney(r?.total_sales ?? "0.00"),
				sortable: false,
			},
			...categoryColumns,
		];
	};

	buildTableData = (items) => {
		return Array.isArray(items) ? [...items] : [];
	};

	getReport = async () => {
		await this.setState({totals: null, loading: true});
		try {
			let model = new API.BuildClass(Types.Locations_Sales._name, API.RequestType.READ);
			this.read = this.request;
			model.filter = buildReportApiFilter(this.state);
			this.state.filter = model.filter;
			const dateRangeFormatted = formatDateRangeLabel(
				this.state.start_date,
				this.state.end_date
			);
			model.search = this.state.search;
			const response = await model.submit(null, this.read);
			const categories = response.categories || [];
			const items = response.items || [];
			const totals = response.totals || null;
			const columns = this.buildColumns(categories);
			const tableData = this.buildTableData(items);
			await this.setState({
				data: tableData,
				categories,
				columns,
				totals,
				dateRangeFormatted,
				key: Math.random(),
			});
			localStorage.setItem(DATE_STORAGE_KEY, this.state.filterDate);
		} finally {
			await this.setState({loading: false});
		}
	};

	render() {
		const {data, pages, loading, limit, columns} = this.state;
		const hasData = data && data.length > 0 && !data.every((r) => r?.row_type === "grand_total");

		return (
			<Card>
				<CardContent>
					<div className="table-bar">
						<ReportDateToolbar
							filterDate={this.state.filterDate}
							start_date={this.state.start_date}
							end_date={this.state.end_date}
							dateRangeFormatted={this.state.dateRangeFormatted}
							showDate={this.state.showDate}
							showStartDate={this.state.showStartDate}
							showEndDate={this.state.showEndDate}
							showDateRange={this.state.showDateRange}
							onToggleModal={this.handleToggleModal}
							onSingleDateChange={this.handleDateChange}
							onStartDateChange={(start_date) => this.setState({start_date})}
							onEndDateChange={(end_date) => this.setState({end_date})}
							onApplyDateRange={this.getReport}
							onCancelDateRange={this.handleCancelDateRange}
							toggleClose={this.toggleClose}
							completedLayawaysOnly={this.state.completedLayawaysOnly}
							completedWorkOrdersOnly={this.state.completedWorkOrdersOnly}
							useLocalTimezone={this.state.useLocalTimezone}
							onLayawayModeChange={this.handleLayawayModeChange}
							onWorkOrderModeChange={this.handleWorkOrderModeChange}
							onTimezoneModeChange={this.handleTimezoneModeChange}
						/>
						<div className="d-flex" style={{paddingTop: 15}}>
							<TextField
								placeholder={"0.00"}
								label={"Sales Report NET Sale"}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals.sales_report_net_sale) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							{columns.length > 0 && this.renderHeaderRight("Employee Sales", columns)}
						</div>
					</div>
					{!loading && !hasData ? (
						<div>There is no data for the dates selected.</div>
					) : (
						<ReactTable
							key={this.state.key}
							columns={columns}
							data={data || []}
							pages={pages}
							loading={loading}
							loadingText="Requesting Data"
							getTrProps={(state, rowInfo) => {
								if (!rowInfo || !rowInfo.row) {
									return {};
								}
								const rowType = rowInfo.row._original?.row_type;
								if (rowType === "seller_subtotal" || rowType === "grand_total") {
									return {style: {fontWeight: "bold"}};
								}
								return {};
							}}
							defaultSorted={[
								{
									id: this.model.sort,
									desc: this.model.order === "ascending",
								},
							]}
							className="-striped -highlight"
							showPaginationTop={false}
							showPaginationBottom={false}
							showPageSizeOptions={true}
							defaultPageSize={limit || 50}
						/>
					)}
					<Framework.Elements.LoadingSpinner isLoading={loading} />
				</CardContent>
			</Card>
		);
	}
}
